import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, Play } from "lucide-react";
import { MaskLines } from "./Motion";
import { waLink } from "@/lib/brand";

const TiltCard = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div className="[perspective:1400px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div
        data-testid="hero-tilt-card"
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, scale: 0.92, y: 40 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
        className="relative aspect-square w-full rounded-[28px] border border-white/10 bg-[#0e1120] shadow-[0_40px_120px_-30px_rgba(139,92,246,.55)]"
      >
        <div className="absolute inset-0 overflow-hidden rounded-[28px]">
          <img src="/img/hero-familia.jpg" alt="Família assistindo Speed Max na TV da sala" className="h-full w-full object-cover object-[62%_center] scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#07080f] via-[#07080f]/10 to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,rgba(139,92,246,.18),transparent_60%)] mix-blend-screen" />
        </div>
        <div style={{ transform: "translateZ(60px)" }} className="absolute left-5 top-5 flex items-center gap-2 rounded-full border border-white/15 bg-black/50 px-3 py-1.5 text-xs backdrop-blur-md">
          <span className="relative flex h-2 w-2"><span className="pulse-ring absolute inset-0 rounded-full bg-red-500" /><span className="relative h-2 w-2 rounded-full bg-red-500" /></span>
          AO VIVO
        </div>
        <div style={{ transform: "translateZ(90px)" }} className="absolute bottom-6 left-6 right-6">
          <p className="font-display text-xs tracking-[0.3em] text-zinc-300">DESDE 2017</p>
          <p className="mt-2 font-display text-2xl sm:text-3xl font-bold leading-tight">Seu play,<br />sem pausa.</p>
        </div>
        <div style={{ transform: "translateZ(120px)" }} className="absolute -right-1 -top-6 sm:-right-8 sm:-top-8 grid h-20 w-20 sm:h-24 sm:w-24 place-items-center rounded-full grad-bg shadow-2xl">
          <Play className="h-8 w-8 fill-white text-white translate-x-0.5" />
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const [wide, setWide] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const on = () => setWide(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  return (
    <section id="topo" ref={ref} data-testid="hero-section" className="grain relative overflow-hidden pt-28 sm:pt-36 pb-12 sm:pb-16">
      <div className="pointer-events-none absolute -left-40 top-10 h-[520px] w-[520px] rounded-full bg-[#3b2bd1]/30 blur-[140px]" />
      <div className="pointer-events-none absolute right-0 bottom-0 h-[420px] w-[420px] rounded-full bg-cyan-500/15 blur-[140px]" />
      <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 gap-14 px-5 sm:px-10 lg:grid-cols-12 lg:gap-10 items-end">
        <motion.div style={wide ? { y: yText } : undefined} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }} className="mb-8 flex items-center gap-3 text-xs tracking-[0.35em] text-zinc-400">
            <span className="h-px w-10 bg-zinc-500" /> STREAMING · TV BOX · ASSISTÊNCIA 24H
          </motion.p>
          <h1 data-testid="hero-title" className="font-display font-extrabold tracking-tight leading-[0.95] text-[13vw] sm:text-6xl lg:text-[5.6rem]">
            <MaskLines lines={["Tudo que", "você ama,"]} />
            <MaskLines lines={["num só play."]} delay={0.24} lineClass="grad-text" />
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1 }} className="mt-6 max-w-md text-base sm:text-lg text-zinc-300">
            5 servidores, assistência 24h e contratação rápida pelo WhatsApp.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 1 }} className="mt-9 flex flex-wrap items-center gap-3">
            <a data-testid="hero-plans-btn" href="#planos" className="group inline-flex items-center gap-3 rounded-full grad-bg px-9 py-5 text-base font-bold text-white shadow-[0_18px_50px_-12px_rgba(139,92,246,.9)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_22px_60px_-10px_rgba(34,211,238,.75)]">
              Contratar agora <ArrowDownRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-[-45deg]" />
            </a>
            <a data-testid="hero-whatsapp-btn" href={waLink("Olá! Quero conhecer os planos da Speed Max.")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-3 rounded-full border border-white/20 px-7 py-5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-white hover:text-[#07080f]">
              Chamar no WhatsApp
            </a>
          </motion.div>
        </motion.div>
        <motion.div style={wide ? { y: yCard } : undefined} className="lg:col-span-5 mx-auto w-full max-w-[440px] lg:max-w-none">
          <TiltCard />
        </motion.div>
      </div>
    </section>
  );
};
